import { EMPTY_ARRAY, formattedDay } from '@talos/kyoko';
import type { PropsWithChildren } from 'react';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { AdjustedSettlementDateResponse } from '../containers/Blotters/CareOrders/adjustedSettlementDateApi';
import { useGetAdjustedSettlementDatesQuery } from '../containers/Blotters/CareOrders/adjustedSettlementDateApi';
import { useFeatureFlag } from '../hooks/useFeatureFlag';

export type IAdjustedSettlementDatesContext = {
  settlementDates: AdjustedSettlementDateResponse[];
};

export const AdjustedSettlementDatesContext = createContext<IAdjustedSettlementDatesContext | undefined>(undefined);

export function useAdjustedSettlementDates() {
  const context = useContext(AdjustedSettlementDatesContext);
  if (!context) {
    throw new Error('useAdjustedSettlementDates must be used within an AdjustedSettlementDatesProvider');
  }
  return context;
}

export function AdjustedSettlementDatesProvider({ children }: PropsWithChildren) {
  const [today, setToday] = useState(() => formattedDay(new Date()));
  const { supportAdjustedSettlementDate } = useFeatureFlag();

  useEffect(() => {
    if (!supportAdjustedSettlementDate) {
      return;
    }
    const interval = setInterval(() => {
      setToday(formattedDay(new Date()));
    }, 60_000);

    return () => clearInterval(interval);
  }, [supportAdjustedSettlementDate]);

  const { data } = useGetAdjustedSettlementDatesQuery(
    { settlementDate: today },
    { skip: !supportAdjustedSettlementDate }
  );

  const value = useMemo(
    () => ({
      settlementDates: data ?? EMPTY_ARRAY,
    }),
    [data]
  );

  return <AdjustedSettlementDatesContext.Provider value={value}>{children}</AdjustedSettlementDatesContext.Provider>;
}
